import { Injector, NgModule } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterModule } from '@angular/router';
import { ClaimGuard, getUrlFromRouterLink, InterceptorGuard } from '@lsq-frontend/common-ui';
import {
    canActivateNode,
    getFirstActiveRouterLink,
    getNodeById,
    getRouterLinkUrlById,
    LsqNoopComponent
} from '@lsq-frontend/common-ui';
import { Routes } from '@lsq-frontend/common-ui';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
    atlas,
    REPORTS,
    REPORTS_ACCOUNT_OVERVIEW,
    REPORTS_COLLECTIONS,
    reportsAtlas,
    ROOT
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { GlobalSearchService } from '../global-search/global-search.service';
import { AccountOverviewByDateComponent } from './account-overview/account-overview-by-date.component';
import { AgingReportComponent } from './aging/aging.report.component';
import { AvailableFundsSummaryComponent } from './available-funds-summary/available-funds-summary.component';
import { CheckNumberResultsReportComponent } from './collections/check-number-results.report.component';
import { CollectionsReportComponent } from './collections/collections.report.component';
import { CreditRequestHistoryReportComponent } from './credit-request-history/credit-request-history.report.component';
import { FundingsReportComponent } from './fundings/fundings.report.component';
import { IneligiblesReportComponent } from './ineligibles/ineligibles.report.component';
import { InvoiceNumberResultsReportComponent } from './invoice-number-results/invoice-number-results.report.component';
import { PurchasesReportComponent } from './purchases/purchases.report.component';
import { ReportsComponent } from './reports.component';
import { UiRoutes } from '@lsq-frontend/angular-shared/app-constants';

export function invoiceNumberResultsInterceptor(
    _snapshot: ActivatedRouteSnapshot,
    injector: Injector
): Observable<boolean> {
    const globalSearchService = injector.get(GlobalSearchService);
    const router = injector.get(Router);
    return globalSearchService.isActive$.pipe(
        tap((isActive) => {
            if (!isActive) {
                router.navigateByUrl(getRouterLinkUrlById(atlas, ROOT));
            }
        })
    );
}

export function checkNumberResultsInterceptor(
    _snapshot: ActivatedRouteSnapshot,
    injector: Injector
): Observable<boolean> {
    const globalSearchService = injector.get(GlobalSearchService);
    const router = injector.get(Router);
    return globalSearchService.isActive$.pipe(
        tap((isActive) => {
            if (!isActive) {
                router.navigateByUrl(getRouterLinkUrlById(atlas, REPORTS_COLLECTIONS));
            }
        })
    );
}

export function rootRedirectInterceptor(
    _snapshot: ActivatedRouteSnapshot,
    injector: Injector
): Observable<boolean> {
    const router = injector.get(Router);
    // If AccountOverview can be activated, redirect there. Else, go to first available child route.
    const accountOverviewNode = getNodeById(reportsAtlas, REPORTS_ACCOUNT_OVERVIEW);
    if (canActivateNode(accountOverviewNode, injector)) {
        router.navigateByUrl(getRouterLinkUrlById(atlas, REPORTS_ACCOUNT_OVERVIEW));
    } else {
        router.navigateByUrl(
            getRouterLinkUrlById(atlas, REPORTS) +
                getUrlFromRouterLink(getFirstActiveRouterLink(reportsAtlas, injector))
        );
    }
    return of(false);
}

export const reportsRoutes: Routes = [
    {
        path: UiRoutes.Home,
        component: ReportsComponent,
        children: [
            {
                path: UiRoutes.Home,
                component: LsqNoopComponent,
                canActivate: [InterceptorGuard],
                data: {
                    interceptor: rootRedirectInterceptor
                }
            },
            {
                path: reportsAtlas.REPORTS_ACCOUNT_OVERVIEW.path,
                component: AccountOverviewByDateComponent,
                canActivate: [ClaimGuard],
                data: {
                    title: reportsAtlas.REPORTS_ACCOUNT_OVERVIEW.title,
                    claims: reportsAtlas.REPORTS_ACCOUNT_OVERVIEW.claims
                }
            },
            {
                path: reportsAtlas.REPORTS_AGING.path,
                component: AgingReportComponent,
                canActivate: [ClaimGuard],
                data: {
                    title: reportsAtlas.REPORTS_AGING.title,
                    claims: reportsAtlas.REPORTS_AGING.claims
                }
            },
            {
                path: reportsAtlas.REPORTS_INELIGIBLES.path,
                component: IneligiblesReportComponent,
                canActivate: [ClaimGuard],
                data: {
                    title: reportsAtlas.REPORTS_INELIGIBLES.title,
                    claims: reportsAtlas.REPORTS_INELIGIBLES.claims
                }
            },
            {
                path: reportsAtlas.REPORTS_FUNDINGS.path,
                component: FundingsReportComponent,
                canActivate: [ClaimGuard],
                data: {
                    title: reportsAtlas.REPORTS_FUNDINGS.title,
                    claims: reportsAtlas.REPORTS_FUNDINGS.claims
                }
            },
            {
                path: reportsAtlas.REPORTS_CREDIT_REQUEST_HISTORY.path,
                component: CreditRequestHistoryReportComponent,
                canActivate: [ClaimGuard],
                data: {
                    title: reportsAtlas.REPORTS_CREDIT_REQUEST_HISTORY.title,
                    claims: reportsAtlas.REPORTS_CREDIT_REQUEST_HISTORY.claims
                }
            },
            {
                path: reportsAtlas.REPORTS_COLLECTIONS.path,
                component: CollectionsReportComponent,
                canActivate: [ClaimGuard],
                data: {
                    title: reportsAtlas.REPORTS_COLLECTIONS.title,
                    claims: reportsAtlas.REPORTS_COLLECTIONS.claims
                }
            },
            {
                path: reportsAtlas.REPORTS_PURCHASES.path,
                component: PurchasesReportComponent,
                canActivate: [ClaimGuard],
                data: {
                    title: reportsAtlas.REPORTS_PURCHASES.title,
                    claims: reportsAtlas.REPORTS_PURCHASES.claims
                }
            },
            {
                path: reportsAtlas.REPORTS_AVAILABLE_FUNDS_SUMMARY.path,
                component: AvailableFundsSummaryComponent,
                canActivate: [ClaimGuard],
                data: {
                    title: reportsAtlas.REPORTS_AVAILABLE_FUNDS_SUMMARY.title,
                    claims: reportsAtlas.REPORTS_AVAILABLE_FUNDS_SUMMARY.claims
                }
            },
            {
                path: reportsAtlas.REPORTS_INVOICE_NUMBER_RESULTS.path,
                component: InvoiceNumberResultsReportComponent,
                canActivate: [ClaimGuard, InterceptorGuard],
                data: {
                    title: reportsAtlas.REPORTS_INVOICE_NUMBER_RESULTS.title,
                    claims: reportsAtlas.REPORTS_INVOICE_NUMBER_RESULTS.claims,
                    interceptor: invoiceNumberResultsInterceptor
                }
            },
            {
                path: reportsAtlas.REPORTS_CHECK_NUMBER_RESULTS.path,
                component: CheckNumberResultsReportComponent,
                canActivate: [ClaimGuard, InterceptorGuard],
                data: {
                    title: reportsAtlas.REPORTS_CHECK_NUMBER_RESULTS.title,
                    claims: reportsAtlas.REPORTS_CHECK_NUMBER_RESULTS.claims,
                    interceptor: checkNumberResultsInterceptor
                }
            }
        ]
    }
];

@NgModule({
    imports: [RouterModule.forChild(reportsRoutes)],
    exports: [RouterModule]
})
export class ReportsRoutingModule {}
